import { useTranslation } from 'react-i18next';
import {
  useState,
  forwardRef,
  useImperativeHandle,
  Ref,
  useCallback,
  useMemo,
} from 'react';
import { TranslationsKeys } from '../../../../../locales/translations';
import { ReportAsyncTypeRow, ReportHistoryItem } from '../types';
import { Modal, Table, ColumnTable, Icon, Button } from '@octano/global-ui';
import { Row, Col } from 'reactstrap';
import dayjs from 'dayjs';

const DownloadCell = ({ url }: { url: string | null }) =>
  url ? (
    <Button
      onClick={() => window.open(url)}
      size="sm"
      icon="download"
      text="Descargar"
    />
  ) : (
    <span style={{ color: '#999', fontSize: '11px' }}>N/A</span>
  );

export type ReportHistoryModalMethods = {
  open: (row: ReportAsyncTypeRow) => void;
};

type ReportHistoryModalProps = Record<string, unknown>;

const ReportHistoryModal = (
  _props: ReportHistoryModalProps,
  ref: Ref<ReportHistoryModalMethods>,
) => {
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_REPORTS_ASYNC);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [report, setReport] = useState<ReportAsyncTypeRow | null>(null);

  const close = useCallback(() => {
    setIsOpen(false);
    setReport(null);
  }, []);

  const open = useCallback((row: ReportAsyncTypeRow) => {
    setReport(row);
    setIsOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({ open }), [open]);

  const columns = useMemo<ColumnTable<ReportHistoryItem>[]>(
    () => [
      {
        columnName: 'date',
        thClassName: 'text-left',
        tdClassName: 'text-left',
        headerText: t('table.historyDate'),
        cellFormat: ({ value }) => dayjs(value).format('DD/MM/YYYY HH:mm'),
      },
      {
        columnName: 'generatedBy',
        thClassName: 'text-left',
        tdClassName: 'text-left',
        headerText: t('table.historyGeneratedBy'),
        cellFormat: ({ value }) => value?.name || t('table.unknownUser'),
      },
      {
        columnName: 'periodOrBreakDate',
        thClassName: 'text-left',
        tdClassName: 'text-left',
        headerText: t('table.historyPeriod'),
        cellFormat: ({ value }) => value || '-',
      },
      {
        columnName: 'url',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: t('table.historyDownload'),
        cellFormat: ({ value }) => <DownloadCell url={value} />,
      },
    ],
    [t],
  );

  const historyData = report?.history || [];

  return (
    <Modal isOpen={isOpen} toggle={close} size="lg" centered unmountOnClose>
      <Row className="text-center mb-3">
        <Col xs={12} className="pb-3">
          <span className="fs-22 text-dark">{t('modal.historyTitle')}</span>
        </Col>
        <Col xs={12}>
          <Icon name="information" size={20} />
          <span className="fs-16 text-dark ml-2">{report?.name || ''}</span>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          {historyData.length === 0 ? (
            <div
              style={{ textAlign: 'center', padding: '40px', color: '#999' }}
            >
              {t('table.noReports')}
            </div>
          ) : (
            <div
              style={{
                maxHeight: '500px',
                overflowY: 'auto',
              }}
            >
              <Table columns={columns} data={historyData} />
            </div>
          )}
        </Col>
      </Row>
      <Row className="mt-3">
        <Col xs={12} className="text-center">
          <Button
            type="button"
            color="secondary"
            onClick={close}
            fullwidth
            text={t('modal.close')}
          />
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(ReportHistoryModal);
